import { Autocomplete, Group, Select, Text } from '@mantine/core';
import { IconStarFilled } from '@tabler/icons-react';
import { Link } from 'react-router-dom';
import { ParticipantAttendanceSelect } from '@/components/ParticipantAttendanceSelect';
import {
  ParticipantCardsPanel,
  type AdHocParticipantInput,
} from '@/components/ParticipantCardsPanel';
import { UnifyCardTable } from '@/components/UnifyCardTable';
import { UnifyTable, type UnifyTableColumn } from '@/components/UnifyTable';
import { COMPACT_TABLE_STYLES } from '@/components/unifyTableVariants';
import type { AppointmentModality } from '@/features/calendar/AppointmentSharedFields';
import {
  locationRequiredForClientAttendance,
  type CalendarClientAttendanceStatus,
} from '@/lib/calendarClientAttendanceStatus';
import type { ParticipantAttendanceFhirStatus } from '@/pages/appointments/utils/participantAttendance';

export interface AppointmentParticipantRow {
  id: string;
  participantRef?: string;
  name: string;
  profileHref?: string;
  email?: string;
  phone?: string;
  modality?: string;
  location?: string;
  attendanceStatus?: string;
  isSubject?: boolean;
  role?: string;
}

export interface AppointmentParticipantsTableProps {
  rows: AppointmentParticipantRow[];
  appearance?: 'inline' | 'card' | 'connectedCards';
  onAttendanceChange?: (patientId: string, status: ParticipantAttendanceFhirStatus) => void;
  savedStatusByPatientId?: Record<string, ParticipantAttendanceFhirStatus | undefined>;
  onLocationChange?: (patientId: string, location: string | undefined) => void;
  locationOptionsByPatientId?: Record<string, string[]>;
  sessionModality?: AppointmentModality;
  onModalityChange?: (modality: AppointmentModality) => void;
  modalityOptions?: { value: string; label: string }[];
  participantOptions?: { value: string; label: string }[];
  onAddParticipant?: (participantRef: string) => void;
  onAddAdHocParticipant?: (input: AdHocParticipantInput) => void | Promise<void>;
  onRemoveParticipant?: (participantRef: string) => void;
  addParticipantDisabled?: boolean;
}

function formatModalityLabel(modality: AppointmentModality | undefined): string {
  if (!modality) return '';
  if (modality === 'in-person') return 'In person';
  if (modality === 'telehealth') return 'Telehealth';
  return 'Hybrid';
}

export function AppointmentParticipantsTable({
  rows,
  appearance = 'inline',
  onAttendanceChange,
  savedStatusByPatientId = {},
  onLocationChange,
  locationOptionsByPatientId = {},
  sessionModality,
  onModalityChange,
  modalityOptions = [],
  participantOptions = [],
  onAddParticipant,
  onAddAdHocParticipant,
  onRemoveParticipant,
  addParticipantDisabled = false,
}: AppointmentParticipantsTableProps): JSX.Element {
  const showSubjectIndicator = rows.some((row) => row.isSubject);

  const renderClientCell = (row: AppointmentParticipantRow, linkable: boolean): JSX.Element => (
    <Group gap={6} wrap="nowrap">
      {showSubjectIndicator ? (
        row.isSubject ? (
          <IconStarFilled size={12} color="var(--mantine-color-gold-6)" aria-label="Subject client" />
        ) : (
          <span style={{ width: 12 }} />
        )
      ) : null}
      {linkable && row.profileHref ? (
        <Text component={Link} to={row.profileHref} size="sm" c="gold.7" fw={500} style={{ textDecoration: 'none' }}>
          {row.name}
        </Text>
      ) : (
        <Text size="sm" fw={500}>
          {row.name}
        </Text>
      )}
    </Group>
  );

  const renderAttendanceCell = (row: AppointmentParticipantRow): JSX.Element => {
    const saved = savedStatusByPatientId[row.id];
    const value =
      (row.attendanceStatus as CalendarClientAttendanceStatus | undefined) ?? saved ?? 'scheduled';
    return (
      <ParticipantAttendanceSelect
        value={value}
        savedStatus={saved ?? 'scheduled'}
        onChange={
          onAttendanceChange
            ? (status) => onAttendanceChange(row.id, status)
            : undefined
        }
        readOnly={!onAttendanceChange}
        aria-label={`Attendance for ${row.name}`}
      />
    );
  };

  if (appearance === 'connectedCards') {
    return (
      <ParticipantCardsPanel
        rows={rows.map((row) => ({
          ...row,
          modality: row.modality || formatModalityLabel(sessionModality) || undefined,
        }))}
        participantOptions={participantOptions}
        onAdd={onAddParticipant}
        onAddAdHoc={onAddAdHocParticipant}
        onRemove={onRemoveParticipant}
        addDisabled={addParticipantDisabled}
        onAttendanceChange={onAttendanceChange}
        savedStatusByParticipantId={savedStatusByPatientId}
        onLocationChange={onLocationChange}
        locationOptionsByParticipantId={locationOptionsByPatientId}
        sessionModality={sessionModality}
        onModalityChange={onModalityChange}
        modalityOptions={modalityOptions}
      />
    );
  }

  if (rows.length === 0) {
    return (
      <Text size="sm" c="dimmed">
        No participants
      </Text>
    );
  }

  const columns: UnifyTableColumn<AppointmentParticipantRow>[] = [
    {
      key: 'name',
      label: 'Client',
      render: (row) => renderClientCell(row, true),
    },
    {
      key: 'email',
      label: 'Email',
      render: (row) => (
        <Text size="sm" c="gray.7" truncate="end" title={row.email} maw={220}>
          {row.email || '—'}
        </Text>
      ),
    },
    {
      key: 'phone',
      label: 'Phone',
      render: (row) => (
        <Text size="sm" c="gray.7" truncate="end" title={row.phone} maw={160}>
          {row.phone || '—'}
        </Text>
      ),
    },
    {
      key: 'modality',
      label: 'Modality',
      render: (row) => {
        if (onModalityChange && sessionModality) {
          return (
            <Select
              size="xs"
              data={modalityOptions}
              value={sessionModality}
              onChange={(next) => {
                if (!next) return;
                onModalityChange(next as AppointmentModality);
              }}
              allowDeselect={false}
              aria-label={`Modality for ${row.name}`}
            />
          );
        }
        return (
          <Text size="sm" c="gray.7" tt="capitalize">
            {row.modality || formatModalityLabel(sessionModality) || '—'}
          </Text>
        );
      },
    },
    {
      key: 'location',
      label: 'Location',
      width: onLocationChange ? 200 : undefined,
      render: (row) => {
        if (!onLocationChange) {
          return (
            <Text size="sm" c="gray.7">
              {row.location || '—'}
            </Text>
          );
        }
        const options = locationOptionsByPatientId[row.id] ?? [];
        const locationData = [
          ...new Set([...options, ...(row.location?.trim() ? [row.location.trim()] : [])]),
        ];
        const attendance =
          (row.attendanceStatus as CalendarClientAttendanceStatus | undefined)
          ?? savedStatusByPatientId[row.id]
          ?? 'scheduled';
        const locationRequired = locationRequiredForClientAttendance(attendance);
        return (
          <Autocomplete
            size="xs"
            data={locationData}
            value={row.location ?? ''}
            placeholder={locationRequired ? 'Required when attended' : 'Optional'}
            required={locationRequired}
            clearable
            aria-label={`Location for ${row.name}`}
            onChange={(v) => onLocationChange(row.id, v.trim() ? v.trim() : undefined)}
          />
        );
      },
    },
    {
      key: 'attendance',
      label: 'Attendance',
      width: onAttendanceChange ? 170 : undefined,
      render: (row) => renderAttendanceCell(row),
    },
  ];

  if (appearance === 'card') {
    return (
      <UnifyCardTable
        data={rows}
        columns={columns}
        getRowKey={(row) => row.id}
      />
    );
  }

  return (
    <UnifyTable
      data={rows}
      columns={columns}
      getRowKey={(row) => row.id}
      withPaper={false}
      scrollMinWidth={0}
      tableProps={{
        fz: 'sm',
        w: '100%',
        styles: COMPACT_TABLE_STYLES,
      }}
    />
  );
}
